import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { JwtService } from './jwt.service';

/** NF525 §2: an unattended terminal must not stay open under someone else's
 *  identity. Short because a POS lives in a dining room, not on a desk.
 *  Only enforced when a device token is present (see `evaluate`) — this
 *  targets enrolled POS terminals, not every logged-in session (e.g. an
 *  admin on the back office shouldn't get auto-locked after 5 min idle). */
export const INACTIVITY_TIMEOUT_MS = 5 * 60_000;
export const LOCK_WARNING_MS = 30_000;
// NF525 §2 again: right after a decisive act (order fired, payment taken) the
// operator is done and the next person must re-identify. The terminal locks on
// a much shorter idle window so it can't stay open under the previous identity.
export const CRITICAL_ACTION_LOCK_MS = 3_000;

// Shared across tabs: the token lives in localStorage, so a tab that locks
// logs out every other tab. Activity in any one of them must count for all.
const ACTIVITY_KEY = 'last_activity_at';
const TICK_MS = 1_000;
const WRITE_THROTTLE_MS = 500;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

export type LockReason = 'inactivity' | 'manual';

@Injectable({ providedIn: 'root' })
export class SessionLockService {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly jwtService = inject(JwtService);

  /** Seconds left before the screen locks, or null when the warning is off. */
  readonly secondsBeforeLock = signal<number | null>(null);

  private ticker: ReturnType<typeof setInterval> | null = null;
  private lastWrite = 0;
  private locking = false;
  private criticalLockArmed = false;

  private readonly onActivity = () => {
    const now = Date.now();
    if (now - this.lastWrite < WRITE_THROTTLE_MS) {
      return;
    }
    this.lastWrite = now;
    localStorage.setItem(ACTIVITY_KEY, String(now));
  };

  start(): void {
    if (this.ticker || typeof document === 'undefined') {
      return;
    }
    this.criticalLockArmed = false;
    this.markActivity();
    // Outside the zone: a pointermove must never schedule a change-detection pass.
    this.zone.runOutsideAngular(() => {
      for (const event of ACTIVITY_EVENTS) {
        document.addEventListener(event, this.onActivity, { passive: true });
      }
      this.ticker = setInterval(() => this.evaluate(), TICK_MS);
    });
  }

  stop(): void {
    if (typeof document !== 'undefined') {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, this.onActivity);
      }
    }
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    this.secondsBeforeLock.set(null);
  }

  markActivity(): void {
    this.lastWrite = 0;
    this.onActivity();
  }

  /** After a decisive POS action: lock on a 3s idle window instead of 5min.
   *  No-op off an enrolled device, where `evaluate` never locks anyway. */
  armCriticalLock(): void {
    this.criticalLockArmed = true;
    this.markActivity();
  }

  /** "I'm still here" from the warning banner. */
  extend(): void {
    this.markActivity();
    this.secondsBeforeLock.set(null);
  }

  private evaluate(): void {
    const onEnrolledDevice = this.jwtService.deviceToken && !this.jwtService.isDeviceTokenExpired();
    if (!this.jwtService.isLogged() || !onEnrolledDevice) {
      if (this.secondsBeforeLock() !== null) {
        this.zone.run(() => this.secondsBeforeLock.set(null));
      }
      return;
    }

    const stored = Number(localStorage.getItem(ACTIVITY_KEY));
    const last = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    const timeout = this.criticalLockArmed ? CRITICAL_ACTION_LOCK_MS : INACTIVITY_TIMEOUT_MS;
    const remaining = timeout - (Date.now() - last);

    if (remaining <= 0) {
      this.zone.run(() => this.lock('inactivity'));
      return;
    }

    // No "still there?" banner for the 3s window: it is a deliberate quick lock,
    // not an idle timeout the operator should be able to dismiss.
    const seconds =
      !this.criticalLockArmed && remaining <= LOCK_WARNING_MS ? Math.ceil(remaining / 1000) : null;
    if (seconds !== this.secondsBeforeLock()) {
      this.zone.run(() => this.secondsBeforeLock.set(seconds));
    }
  }

  async lock(reason: LockReason = 'manual'): Promise<void> {
    if (this.locking) {
      return;
    }
    this.locking = true;
    this.criticalLockArmed = false;
    this.stop();

    // Journaled before the token is dropped — the interceptor needs it. A
    // journal that is unreachable must not be able to keep a screen unlocked.
    try {
      await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/lock`, { reason }));
    } catch {
      /* deliberately ignored */
    }

    // On a POS the device stays enrolled: locking returns to the PIN pad, it
    // does not unbind the terminal from its restaurant.
    if (this.jwtService.deviceToken && !this.jwtService.isDeviceTokenExpired()) {
      this.jwtService.clearEmployeeToken();
      await this.router.navigateByUrl('/pin-entry');
    } else {
      this.jwtService.unsetToken();
    }

    this.locking = false;
  }
}
